import { useEffect, useRef } from 'react';

// Starfield Animation
export const StarfieldBackground = ({ color = '#06b6d4', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    const stars = [];
    const numStars = 150;
    
    const initStars = () => {
      stars.length = 0;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      for (let i = 0; i < numStars; i++) {
        stars.push({
          x: (Math.random() - 0.5) * canvas.width * 2 + centerX,
          y: (Math.random() - 0.5) * canvas.height * 2 + centerY,
          z: Math.random() * canvas.width,
          size: Math.random() * 2 + 0.5
        });
      }
    };
    initStars();
    
    let animationId;
    function animate() {
      ctx.fillStyle = 'rgba(10, 10, 20, 0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      stars.forEach(star => {
        star.z -= 3;
        if (star.z <= 0) {
          star.z = canvas.width;
          star.x = (Math.random() - 0.5) * canvas.width * 2 + centerX;
          star.y = (Math.random() - 0.5) * canvas.height * 2 + centerY;
        }
        
        const k = 128.0 / star.z;
        const px = (star.x - centerX) * k + centerX;
        const py = (star.y - centerY) * k + centerY;
        const size = (1 - star.z / canvas.width) * star.size * 3;
        
        if (px >= 0 && px <= canvas.width && py >= 0 && py <= canvas.height) {
          const alpha = 1 - star.z / canvas.width;
          ctx.fillStyle = hexToRgba(color, alpha);
          ctx.beginPath();
          ctx.arc(px, py, size, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      
      animationId = requestAnimationFrame(animate);
    }
    
    animate();
    
    const handleResize = () => {
      updateSize();
      initStars();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: 'linear-gradient(180deg, #0a0a14 0%, #141428 100%)' }}
    />
  );
};

// Helper function to convert hex to rgba
const hexToRgba = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Matrix Rain Animation
export const MatrixBackground = ({ color = '#22c55e', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    const chars = 'アイウエオカキクケコサシスセソタチツテト0123456789ABCDEF';
    const fontSize = 12;
    let columns = Math.floor(canvas.width / fontSize);
    let drops = [];
    
    const initDrops = () => {
      columns = Math.floor(canvas.width / fontSize);
      drops = [];
      for (let i = 0; i < columns; i++) {
        drops[i] = Math.random() * -50;
      }
    };
    initDrops();
    
    let animationId;
    function animate() {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const char = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        
        // Bright leading character
        ctx.fillStyle = color;
        ctx.globalAlpha = 1;
        ctx.fillText(char, x, y);
        
        // Trailing characters
        ctx.globalAlpha = 0.6;
        ctx.fillText(chars[Math.floor(Math.random() * chars.length)], x, y - fontSize);
        ctx.globalAlpha = 0.3;
        ctx.fillText(chars[Math.floor(Math.random() * chars.length)], x, y - fontSize * 2);
        
        ctx.globalAlpha = 1;
        
        if (y > canvas.height && Math.random() > 0.98) {
          drops[i] = 0;
        }
        drops[i] += 0.5;
      }
      
      animationId = requestAnimationFrame(animate);
    }
    
    // Initial fill
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    animate();
    
    const handleResize = () => {
      updateSize();
      initDrops();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: '#000000' }}
    />
  );
};

// Particle Float Animation
export const ParticleBackground = ({ color = '#06b6d4', color2 = '#d946ef', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    const particles = [];
    const numParticles = 60;
    
    const initParticles = () => {
      particles.length = 0;
      for (let i = 0; i < numParticles; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.8,
          vy: (Math.random() - 0.5) * 0.8,
          size: Math.random() * 3 + 2,
          color: Math.random() > 0.5 ? color : color2,
          opacity: Math.random() * 0.5 + 0.4
        });
      }
    };
    initParticles();
    
    let animationId;
    function animate() {
      ctx.fillStyle = 'rgba(10, 10, 20, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
        
        // Connect nearby particles
        particles.forEach((p2, j) => {
          if (i >= j) return;
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = p.color;
            ctx.globalAlpha = (100 - dist) / 100 * 0.3;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
        
        ctx.globalAlpha = 1;
      });
      
      animationId = requestAnimationFrame(animate);
    }
    
    animate();
    
    const handleResize = () => {
      updateSize();
      initParticles();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, color2, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: 'linear-gradient(180deg, #0a0a14 0%, #0f0a1a 100%)' }}
    />
  );
};

// Aurora Waves Animation
export const AuroraBackground = ({ color = '#06b6d4', color2 = '#a855f7', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    let time = 0;
    let animationId;
    
    function animate() {
      ctx.fillStyle = 'rgba(5, 5, 15, 0.03)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw aurora waves
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.moveTo(0, canvas.height * 0.3);
        
        for (let x = 0; x <= canvas.width; x += 5) {
          const y = canvas.height * 0.3 + 
            Math.sin(x * 0.008 + time + i * 0.5) * 40 +
            Math.sin(x * 0.015 + time * 1.5 + i) * 25 +
            i * 30;
          ctx.lineTo(x, y);
        }
        
        ctx.lineTo(canvas.width, canvas.height);
        ctx.lineTo(0, canvas.height);
        ctx.closePath();
        
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
        const alpha = 0.12 - i * 0.02;
        gradient.addColorStop(0, hexToRgba(color, alpha));
        gradient.addColorStop(0.3, hexToRgba(color2, alpha));
        gradient.addColorStop(0.6, hexToRgba('#22c55e', alpha));
        gradient.addColorStop(1, hexToRgba(color, alpha));
        
        ctx.fillStyle = gradient;
        ctx.fill();
      }
      
      time += 0.015;
      animationId = requestAnimationFrame(animate);
    }
    
    // Initial fill
    ctx.fillStyle = '#050510';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    animate();
    
    const handleResize = () => {
      updateSize();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, color2, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: '#050510' }}
    />
  );
};

// Fireflies Animation
export const FirefliesBackground = ({ color = '#fbbf24', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    const fireflies = [];
    const numFireflies = 40;
    
    const initFireflies = () => {
      fireflies.length = 0;
      for (let i = 0; i < numFireflies; i++) {
        fireflies.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 1.5,
          vy: (Math.random() - 0.5) * 1.5,
          size: Math.random() * 3 + 2,
          phase: Math.random() * Math.PI * 2,
          speed: Math.random() * 0.03 + 0.02
        });
      }
    };
    initFireflies();
    
    let animationId;
    function animate() {
      ctx.fillStyle = 'rgba(5, 15, 5, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      fireflies.forEach(f => {
        f.x += f.vx;
        f.y += f.vy;
        f.phase += f.speed;
        
        // Bounce off edges with padding
        if (f.x < 20 || f.x > canvas.width - 20) f.vx *= -1;
        if (f.y < 20 || f.y > canvas.height - 20) f.vy *= -1;
        
        // Keep within bounds
        f.x = Math.max(10, Math.min(canvas.width - 10, f.x));
        f.y = Math.max(10, Math.min(canvas.height - 10, f.y));
        
        // Random direction changes
        if (Math.random() < 0.02) {
          f.vx += (Math.random() - 0.5) * 0.8;
          f.vy += (Math.random() - 0.5) * 0.8;
        }
        
        // Limit speed
        const speed = Math.sqrt(f.vx * f.vx + f.vy * f.vy);
        if (speed > 2) {
          f.vx = (f.vx / speed) * 2;
          f.vy = (f.vy / speed) * 2;
        }
        
        const glow = (Math.sin(f.phase) + 1) / 2;
        
        // Glow effect
        const gradient = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.size * 6);
        gradient.addColorStop(0, hexToRgba(color, glow * 0.9));
        gradient.addColorStop(0.4, hexToRgba(color, glow * 0.4));
        gradient.addColorStop(1, 'rgba(251, 191, 36, 0)');
        
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.size * 6, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
        
        // Core
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.size * glow + 1, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 200, ${glow * 0.9 + 0.1})`;
        ctx.fill();
      });
      
      animationId = requestAnimationFrame(animate);
    }
    
    // Initial fill
    ctx.fillStyle = '#050f05';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    animate();
    
    const handleResize = () => {
      updateSize();
      initFireflies();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: 'linear-gradient(180deg, #050f05 0%, #0a1a0a 100%)' }}
    />
  );
};

// Bubbles Animation
export const BubblesBackground = ({ color = '#06b6d4', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    const bubbles = [];
    const numBubbles = 30;
    
    const initBubbles = () => {
      bubbles.length = 0;
      for (let i = 0; i < numBubbles; i++) {
        bubbles.push({
          x: Math.random() * canvas.width,
          y: canvas.height + Math.random() * canvas.height,
          size: Math.random() * 20 + 8,
          speed: Math.random() * 1.5 + 0.5,
          wobble: Math.random() * Math.PI * 2,
          wobbleSpeed: Math.random() * 0.03 + 0.01
        });
      }
    };
    initBubbles();
    
    let animationId;
    function animate() {
      ctx.fillStyle = 'rgba(10, 20, 40, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      bubbles.forEach(b => {
        b.y -= b.speed;
        b.wobble += b.wobbleSpeed;
        b.x += Math.sin(b.wobble) * 0.8;
        
        if (b.y < -b.size * 2) {
          b.y = canvas.height + b.size * 2;
          b.x = Math.random() * canvas.width;
        }
        
        // Bubble outline
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.size, 0, Math.PI * 2);
        ctx.strokeStyle = hexToRgba(color, 0.4);
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Bubble fill
        const gradient = ctx.createRadialGradient(b.x - b.size * 0.3, b.y - b.size * 0.3, 0, b.x, b.y, b.size);
        gradient.addColorStop(0, hexToRgba(color, 0.1));
        gradient.addColorStop(1, 'rgba(6, 182, 212, 0)');
        ctx.fillStyle = gradient;
        ctx.fill();
        
        // Highlight
        ctx.beginPath();
        ctx.arc(b.x - b.size * 0.35, b.y - b.size * 0.35, b.size * 0.2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.fill();
      });
      
      animationId = requestAnimationFrame(animate);
    }
    
    // Initial fill
    ctx.fillStyle = '#0a1428';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    animate();
    
    const handleResize = () => {
      updateSize();
      initBubbles();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: 'linear-gradient(180deg, #0a1428 0%, #0c1929 100%)' }}
    />
  );
};

// Geometric Shapes Animation
export const GeometricBackground = ({ color = '#06b6d4', color2 = '#d946ef', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    const shapes = [];
    const numShapes = 15;
    
    const initShapes = () => {
      shapes.length = 0;
      for (let i = 0; i < numShapes; i++) {
        shapes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 40 + 20,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.02,
          sides: Math.floor(Math.random() * 4) + 3,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          color: Math.random() > 0.5 ? color : color2,
          opacity: Math.random() * 0.25 + 0.1
        });
      }
    };
    initShapes();
    
    let animationId;
    function animate() {
      ctx.fillStyle = 'rgba(10, 10, 20, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      shapes.forEach(s => {
        s.x += s.vx;
        s.y += s.vy;
        s.rotation += s.rotationSpeed;
        
        if (s.x < -s.size) s.x = canvas.width + s.size;
        if (s.x > canvas.width + s.size) s.x = -s.size;
        if (s.y < -s.size) s.y = canvas.height + s.size;
        if (s.y > canvas.height + s.size) s.y = -s.size;
        
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.rotation);
        
        ctx.beginPath();
        for (let i = 0; i < s.sides; i++) {
          const angle = (i / s.sides) * Math.PI * 2;
          const x = Math.cos(angle) * s.size;
          const y = Math.sin(angle) * s.size;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        
        ctx.strokeStyle = s.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = s.opacity;
        ctx.stroke();
        
        ctx.restore();
        ctx.globalAlpha = 1;
      });
      
      animationId = requestAnimationFrame(animate);
    }
    
    // Initial fill
    ctx.fillStyle = '#0a0a14';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    animate();
    
    const handleResize = () => {
      updateSize();
      initShapes();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, color2, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: 'linear-gradient(180deg, #0a0a14 0%, #141428 100%)' }}
    />
  );
};

// Rain Animation
export const RainBackground = ({ color = '#64b5f6', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    const drops = [];
    const numDrops = 150;
    
    const initDrops = () => {
      drops.length = 0;
      for (let i = 0; i < numDrops; i++) {
        drops.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          length: Math.random() * 15 + 8,
          speed: Math.random() * 8 + 6,
          opacity: Math.random() * 0.4 + 0.1
        });
      }
    };
    initDrops();
    
    let animationId;
    function animate() {
      ctx.fillStyle = 'rgba(10, 15, 25, 0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      drops.forEach(d => {
        d.y += d.speed;
        
        if (d.y > canvas.height) {
          d.y = -d.length;
          d.x = Math.random() * canvas.width;
        }
        
        ctx.beginPath();
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x, d.y + d.length);
        ctx.strokeStyle = hexToRgba(color, d.opacity);
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });
      
      animationId = requestAnimationFrame(animate);
    }
    
    animate();
    
    const handleResize = () => {
      updateSize();
      initDrops();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: 'linear-gradient(180deg, #0a0f19 0%, #151f2e 100%)' }}
    />
  );
};

// Snow Animation
export const SnowBackground = ({ color = '#ffffff', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    const flakes = [];
    const numFlakes = 100;
    
    const initFlakes = () => {
      flakes.length = 0;
      for (let i = 0; i < numFlakes; i++) {
        flakes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 3 + 1.5,
          speed: Math.random() * 1.5 + 0.5,
          wobble: Math.random() * Math.PI * 2,
          wobbleSpeed: Math.random() * 0.02 + 0.01,
          opacity: Math.random() * 0.6 + 0.4
        });
      }
    };
    initFlakes();
    
    let animationId;
    function animate() {
      ctx.fillStyle = 'rgba(15, 20, 35, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      flakes.forEach(f => {
        f.y += f.speed;
        f.wobble += f.wobbleSpeed;
        f.x += Math.sin(f.wobble) * 0.6;
        
        if (f.y > canvas.height) {
          f.y = -f.size;
          f.x = Math.random() * canvas.width;
        }
        
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.size, 0, Math.PI * 2);
        ctx.fillStyle = hexToRgba(color, f.opacity);
        ctx.fill();
      });
      
      animationId = requestAnimationFrame(animate);
    }
    
    animate();
    
    const handleResize = () => {
      updateSize();
      initFlakes();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: 'linear-gradient(180deg, #0f1423 0%, #1a2535 100%)' }}
    />
  );
};

// Waves Animation
export const WavesBackground = ({ color = '#06b6d4', containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef?.current || canvas.parentElement;
    
    const updateSize = () => {
      canvas.width = container?.clientWidth || window.innerWidth;
      canvas.height = container?.clientHeight || window.innerHeight;
    };
    updateSize();
    
    let time = 0;
    let animationId;
    
    function animate() {
      ctx.fillStyle = '#0a0a14';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw multiple wave layers
      for (let layer = 0; layer < 4; layer++) {
        ctx.beginPath();
        ctx.moveTo(0, canvas.height);
        
        for (let x = 0; x <= canvas.width; x += 3) {
          const y = canvas.height - 80 - layer * 40 +
            Math.sin(x * 0.015 + time + layer * 0.5) * 25 +
            Math.sin(x * 0.025 + time * 1.5 + layer) * 15;
          ctx.lineTo(x, y);
        }
        
        ctx.lineTo(canvas.width, canvas.height);
        ctx.closePath();
        
        const gradient = ctx.createLinearGradient(0, canvas.height - 150, 0, canvas.height);
        const alpha = 0.25 - layer * 0.05;
        gradient.addColorStop(0, hexToRgba(color, alpha));
        gradient.addColorStop(1, hexToRgba(color, alpha * 0.3));
        
        ctx.fillStyle = gradient;
        ctx.fill();
      }
      
      time += 0.025;
      animationId = requestAnimationFrame(animate);
    }
    
    animate();
    
    const handleResize = () => {
      updateSize();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [color, containerRef]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
    />
  );
};

export default {
  StarfieldBackground,
  MatrixBackground,
  ParticleBackground,
  AuroraBackground,
  FirefliesBackground,
  BubblesBackground,
  GeometricBackground,
  RainBackground,
  SnowBackground,
  WavesBackground
};
